/*
 * Co-located server-data hooks for the super-admin Users surface (U6/U7).
 *
 * `GET /auth/users` lists the GLOBAL user roster (super-admin only);
 * `PATCH /auth/users/{user_id}` toggles a user's global `disabled` flag (blocks
 * login everywhere). Both are super-admin-gated server-side from a fresh
 * `users.is_super_admin` read. The disable mutation invalidates the roster so the
 * table reflects the new state.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { listUsers, updateUser } from "../../../api/endpoints";
import type {
  UserRosterEntry,
  UpdateUserRequest,
} from "../../../api/client";

export const usersKeys = {
  all: ["users"] as const,
} as const;

/** List ALL users (super-admin). 403 otherwise. */
export function useUsers(
  options?: Partial<UseQueryOptions<UserRosterEntry[]>>,
) {
  return useQuery({
    queryKey: usersKeys.all,
    queryFn: () => listUsers(),
    ...options,
  });
}

interface UpdateUserVars {
  userId: string;
  body: UpdateUserRequest;
}

/**
 * Toggle a user's global `disabled` flag (super-admin). 409 when disabling a
 * super-admin or oneself.
 */
export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation<UserRosterEntry, unknown, UpdateUserVars>({
    mutationFn: ({ userId, body }) => updateUser(userId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}
